import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Loader2 } from "lucide-react";
import AuthShell from "../components/AuthShell";
import GoogleIcon from "../components/GoogleIcon";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../context/AuthContext";
import { useAdminAuth } from "../context/AdminAuthContext";

const GOOGLE_ERRORS = {
  "email-registered":
    "This email is already registered with email/password. Please sign in with your password.",
  "google-auth-failed": "Google authentication failed. Please try again.",
  "account-deactivated":
    "Your account has been deactivated. Please contact botibongasolutions@gmail.com to appeal.",
};

export default function SignIn() {
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const { setPendingAdminEmail } = useAdminAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");

  // Handle Google OAuth error redirects
  useEffect(() => {
    const err = searchParams.get("error");
    if (err && GOOGLE_ERRORS[err]) {
      setError(GOOGLE_ERRORS[err]);
    }
  }, [searchParams]);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) {
      setError("Please enter your email and password");
      return;
    }
    setLoading(true);
    try {
      const data = await signInWithEmail(form);
      if (data?.route === "admin") {
        setPendingAdminEmail(form.email.trim().toLowerCase());
        navigate("/admin/verify-2fa");
        return;
      }
      navigate("/verify");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    setGoogleLoading(true);
    signInWithGoogle();
    // Browser navigates away — spinner stays visible until page unloads.
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to manage your properties and tenants."
      footer={
        <>
          Don’t have an account?{" "}
          <Link to="/signup" className="font-medium text-indigo-600 hover:text-indigo-700">
            Sign up
          </Link>
          <br />
          <span className="mt-2 inline-block text-xs text-gray-400">
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Terms of Service</a>
            {" · "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-600">Privacy Policy</a>
          </span>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="email" className="label-base">
            Email
          </label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@company.com"
              className="input-base pl-9"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="label-base">
              Password
            </label>
            <Link to="/forgot-password" className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
              Forgot password?
            </Link>
          </div>
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={form.password}
            onChange={update("password")}
            placeholder="••••••••"
          />
        </div>
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        <button type="submit" className="btn-primary w-full" disabled={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Sign in
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-gray-200" />
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
          Or
        </span>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      <button
        onClick={onGoogle}
        className="btn-secondary w-full"
        disabled={googleLoading}
      >
        {googleLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleIcon className="h-5 w-5" />
        )}
        Sign in with Google
      </button>
    </AuthShell>
  );
}
